import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { excerpt } from '../../utils/richtext';
import { useNavigate, useParams, useSearchParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { dirAttr, resolveLocalized } from '../../i18n/contentLocales';
import {
  Avatar,
  Box,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  LinearProgress,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ListIcon from '@mui/icons-material/List';
import MapIcon from '@mui/icons-material/Map';
import { MapContainer, Marker, Popup, TileLayer } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import {
  listRunChallenges,
  listRunStations,
  listSubmissions,
} from '../../api/runs';
import { ApiError } from '../../api/client';
import { useAuth } from '../../auth/useAuth';
import type { MultilingualString, RunChallenge, RunStation, Submission } from '../../api/types';
import ErrorAlert from '../../components/ErrorAlert';
import LoadingSpinner from '../../components/LoadingSpinner';
import StationAddress from '../../components/StationAddress';
import { useAutoRefresh } from '../../hooks/useAutoRefresh';

type ChallengesByStation = Map<string, RunChallenge[]>;
type SubmittedChallengeIds = Set<string>;

// Numbered circle markers (issue #258) — the station's position in the fixed
// route order, so the map matches the numbered list. divIcon instead of the
// stock Leaflet pin; done-stations turn green.
const numberedStationIcon = (n: number, allDone: boolean) =>
  L.divIcon({
    className: '', // suppress the default divIcon white box
    html: `<div style="
      width: 30px; height: 30px; border-radius: 50%;
      background: ${allDone ? '#2e7d32' : '#1976d2'};
      color: #fff; font: 700 14px/30px Roboto, sans-serif; text-align: center;
      border: 2px solid #fff; box-shadow: 0 1px 4px rgba(0,0,0,0.4);
    ">${n}</div>`,
    iconSize: [30, 30],
    iconAnchor: [15, 15],
    popupAnchor: [0, -16],
  });

const StationList: React.FC = () => {
  const { runId } = useParams<{ runId: string }>();
  const { user } = useAuth();
  const { t, i18n } = useTranslation('runs');
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [stations, setStations] = useState<RunStation[]>([]);
  const [challengesByStation, setChallengesByStation] = useState<ChallengesByStation>(new Map());
  const [submittedIds, setSubmittedIds] = useState<SubmittedChallengeIds>(new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Persisted in the URL (not just local state) so navigating to a station
  // and back can restore Map vs. List instead of always resetting to List.
  const mode: 'list' | 'map' = searchParams.get('view') === 'map' ? 'map' : 'list';
  const setMode = (next: 'list' | 'map') => {
    setSearchParams(next === 'map' ? { view: 'map' } : {}, { replace: true });
  };

  const orgSlug = user?.organization_slug;

  const load = useCallback(async ({ quiet = false }: { quiet?: boolean } = {}) => {
    if (!orgSlug || !runId) return;
    if (!quiet) setLoading(true);
    setError(null);
    try {
      const [stationsResp, submissionsResp] = await Promise.all([
        listRunStations(orgSlug, runId),
        listSubmissions(orgSlug, runId),
      ]);
      const sorted = [...stationsResp.items].sort((a, b) => a.sort_order - b.sort_order);
      setStations(sorted);
      setSubmittedIds(new Set(submissionsResp.items.map((s: Submission) => s.run_challenge_id)));

      const challengeEntries = await Promise.all(
        sorted.map(async (s) => {
          const resp = await listRunChallenges(orgSlug, runId, s.id);
          const sortedChallenges = [...resp.items].sort((a, b) => a.sort_order - b.sort_order);
          return [s.id, sortedChallenges] as const;
        }),
      );
      setChallengesByStation(new Map(challengeEntries));
    } catch (err) {
      setError(err instanceof ApiError ? err.detail : String(err));
    } finally {
      setLoading(false);
    }
  }, [orgSlug, runId]);

  useEffect(() => {
    void load();
  }, [load]);

  useAutoRefresh(() => load({ quiet: true }));

  // Full UI language (not collapsed to de/en): org content locales are free
  // (e.g. ar, fa) — an Arabic-browser participant gets Arabic content, RTL.
  const activeLang = i18n.language;
  const titleOf = (ml: MultilingualString) => resolveLocalized(ml, activeLang).text;
  const dirOf = (ml: MultilingualString) => dirAttr(resolveLocalized(ml, activeLang).locale);

  const progress = useMemo(() => {
    const perStation = stations.map((s) => {
      const chs = challengesByStation.get(s.id) ?? [];
      const done = chs.filter((c) => submittedIds.has(c.id)).length;
      const points = chs.reduce(
        (n, c) => n + (typeof c.scoring_rules?.max_points === 'number' ? c.scoring_rules.max_points : 0),
        0,
      );
      return { station: s, done, total: chs.length, points };
    });
    // stations is sorted by sort_order, so the array index is the route number.
    const totalDone = perStation.reduce((n, p) => n + p.done, 0);
    const totalTotal = perStation.reduce((n, p) => n + p.total, 0);
    return {
      perStation,
      totalDone,
      totalTotal,
      percent: totalTotal > 0 ? Math.round((totalDone / totalTotal) * 100) : 0,
    };
  }, [stations, challengesByStation, submittedIds]);

  if (loading) return <LoadingSpinner />;

  const stationsWithCoords = progress.perStation.filter(
    ({ station }) =>
      typeof station.location_lat === 'number' && typeof station.location_lng === 'number',
  );
  const mapCentre: [number, number] =
    stationsWithCoords.length > 0
      ? [
          stationsWithCoords[0].station.location_lat as number,
          stationsWithCoords[0].station.location_lng as number,
        ]
      : [48.3705, 10.8978]; // Augsburg fallback

  return (
    <Stack spacing={2}>
      <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'baseline' }}>
        <Typography variant="h2" component="h1">
          {t('participant.stations.title')}
        </Typography>
        {stations.length > 0 && (
          <Typography variant="body2" color="text.secondary">
            {t('participant.stations.stationCount', { count: stations.length })}
          </Typography>
        )}
      </Stack>

      {stations.length > 0 && (
        <ToggleButtonGroup
          value={mode}
          exclusive
          size="small"
          onChange={(_, next) => next && setMode(next)}
          aria-label={t('participant.stations.viewToggle', { defaultValue: 'Ansicht' })}
        >
          <ToggleButton value="list" aria-label="list">
            <ListIcon fontSize="small" sx={{ mr: 0.5 }} />
            {t('participant.stations.listView', { defaultValue: 'Liste' })}
          </ToggleButton>
          <ToggleButton value="map" aria-label="map">
            <MapIcon fontSize="small" sx={{ mr: 0.5 }} />
            {t('participant.stations.mapView', { defaultValue: 'Karte' })}
          </ToggleButton>
        </ToggleButtonGroup>
      )}

      {error && <ErrorAlert message={error} onRetry={load} />}

      {stations.length > 0 && (
        <Box>
          <Stack direction="row" sx={{ justifyContent: 'space-between', mb: 0.5 }}>
            <Typography variant="body2" color="text.secondary">
              {progress.totalDone} / {progress.totalTotal}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {progress.percent}%
            </Typography>
          </Stack>
          <LinearProgress
            variant="determinate"
            value={progress.percent}
            sx={{ height: 8, borderRadius: 4 }}
          />
        </Box>
      )}

      {stations.length === 0 && !error && (
        <Card sx={{ textAlign: 'center', py: 4 }}>
          <CardContent>
            <Typography variant="body1" color="text.secondary">
              {t('participant.stations.noStationsYet')}
            </Typography>
          </CardContent>
        </Card>
      )}

      {mode === 'map' && stations.length > 0 && (
        <Box
          sx={{
            height: 420,
            borderRadius: 2,
            overflow: 'hidden',
            border: 1,
            borderColor: 'divider',
          }}
        >
          {stationsWithCoords.length === 0 ? (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                {t('participant.stations.noCoords', {
                  defaultValue: 'Für diese Rallye sind noch keine Standorte hinterlegt.',
                })}
              </Typography>
            </Box>
          ) : (
            <MapContainer
              center={mapCentre}
              zoom={14}
              style={{ height: '100%', width: '100%' }}
              scrollWheelZoom
            >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              {stationsWithCoords.map(({ station, done, total }) => (
                <Marker
                  key={station.id}
                  position={[station.location_lat as number, station.location_lng as number]}
                  icon={numberedStationIcon(
                    stations.findIndex((s) => s.id === station.id) + 1,
                    total > 0 && done === total,
                  )}
                  eventHandlers={{
                    click: () => navigate(`/runs/${runId}/participant/stations/${station.id}?from=map`),
                  }}
                >
                  <Popup>
                    <Typography variant="subtitle2" sx={{ mb: 0.5 }} dir={dirOf(station.title)}>
                      {stations.findIndex((s) => s.id === station.id) + 1}.{' '}
                      {titleOf(station.title) ||
                        t('participant.stations.untitled', { defaultValue: 'Station' })}
                    </Typography>
                    {total > 0 && (
                      <Typography variant="caption" color="text.secondary" component="div">
                        {t('participant.stations.tasksDone', { done, total })}
                      </Typography>
                    )}
                    {station.address && (
                      <StationAddress
                        address={station.address}
                        lat={station.location_lat}
                        lng={station.location_lng}
                        label={titleOf(station.title)}
                        dense
                      />
                    )}
                  </Popup>
                </Marker>
              ))}
            </MapContainer>
          )}
        </Box>
      )}

      {mode === 'list' && progress.perStation.map(({ station, done, total, points }, idx) => {
        const allDone = total > 0 && done === total;
        const description = excerpt(titleOf(station.description));
        return (
          <Card key={station.id} variant="outlined">
            <CardActionArea
              onClick={() => navigate(`/runs/${runId}/participant/stations/${station.id}`)}
              aria-label={t('participant.stations.viewStation')}
            >
              <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar
                  sx={{
                    width: 40,
                    height: 40,
                    bgcolor: allDone ? 'tertiary.main' : 'primary.main',
                    color: allDone ? 'tertiary.contrastText' : 'primary.contrastText',
                    fontWeight: 700,
                  }}
                  aria-hidden="true"
                >
                  {allDone ? <CheckIcon /> : idx + 1}
                </Avatar>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="subtitle1" component="div" sx={{ fontWeight: 600 }} dir={dirOf(station.title)}>
                    {titleOf(station.title) || t('participant.stations.untitled', { defaultValue: `Station ${idx + 1}` })}
                  </Typography>
                  {description && (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                      }}
                      dir={dirOf(station.description)}
                    >
                      {description}
                    </Typography>
                  )}
                  <Stack direction="row" spacing={1} sx={{ mt: 0.75, flexWrap: 'wrap', rowGap: 0.5 }}>
                    {total > 0 && (
                      <Chip
                        label={t('participant.stations.tasksDone', { done, total })}
                        size="small"
                        color={allDone ? 'success' : 'default'}
                        variant={allDone ? 'filled' : 'outlined'}
                      />
                    )}
                    {points > 0 && (
                      <Chip
                        label={t('participant.leaderboard.points', { points })}
                        size="small"
                        variant="outlined"
                      />
                    )}
                  </Stack>
                </Box>
                <ChevronRightIcon color="action" sx={{ flexShrink: 0 }} />
              </CardContent>
            </CardActionArea>
          </Card>
        );
      })}
    </Stack>
  );
};

export default StationList;
